import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Users, Search, Filter, ArrowUpDown, Loader, Server, ChevronDown } from 'lucide-react';
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';

interface UserStats {
  username: string;
  engagement_score: number;
  percent_change_24h: number;
  profile_image: string;
  sevenDayEngagement?: number; // Added for 7-day engagement percentage
}

interface SquarePosition {
  x: number;
  y: number;
  width: number;
  height: number;
  user: UserStats;
  rank: number;
}

export default function KOLScanPage() {
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState<UserStats[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [squares, setSquares] = useState<SquarePosition[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [hoveredSquare, setHoveredSquare] = useState<SquarePosition | null>(null);
  const [itemCount] = useState<number>(50); // Always fixed at 50
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        setLoadingStats(true);
        const userStatsRef = collection(db, 'user_stats');
        const q = query(userStatsRef, orderBy('engagement_score', 'desc'), limit(50));
        const snapshot = await getDocs(q);
        
        // Fetch user stats and 7-day engagement data
        const userPromises = snapshot.docs.map(async (userDocSnapshot) => {
          const data = userDocSnapshot.data() as UserStats;
          const username = userDocSnapshot.id;
          
          // Fetch 7-day engagement data - try to get the most recent document
          // First try the most recent dates in descending order
          const possibleDates = ['2025-06-06', '2025-06-03', '2025-05-30'];
          let sevenDayEngagement = 0;
          
          for (const dateStr of possibleDates) {
            const engagementDocRef = doc(db, 'user_engagement_history', username, '7_day_engagement', dateStr);
            const engagementDocSnap = await getDoc(engagementDocRef);
            
            if (engagementDocSnap.exists()) {
              const engagementData = engagementDocSnap.data();
              sevenDayEngagement = engagementData.engagement_share_percentage || 0;
              break; // Found data, no need to check older dates
            }
          }
          
          return {
            username: userDocSnapshot.id,
            engagement_score: data.engagement_score || 0,
            percent_change_24h: data.percent_change_24h || 0,
            profile_image: data.profile_image || '',
            sevenDayEngagement
          };
        });
        
        const usersWithEngagement = await Promise.all(userPromises);
        setUserStats(usersWithEngagement);
      } catch (error) {
        console.error('Error fetching user stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchUserStats();
  }, []);

  // Calculate squares based on selected item count and container size
  const calculateSquares = useMemo(() => {
    return () => {
      if (!containerRef.current || userStats.length === 0) return;
      
      const container = containerRef.current;
      const width = container.clientWidth;
      
      const isMobile = window.innerWidth < 768;
      const maxDesktopHeight = 600;
      const aspectRatio = isMobile ? 1.2 : 0.8;
      
      const calculatedHeight = width * aspectRatio;
      const height = isMobile ? calculatedHeight : Math.min(calculatedHeight, maxDesktopHeight);
      
      setContainerSize({ width, height });
      
      // Take top N users based on selected itemCount (always 50)
      const topUsers = userStats.slice(0, 50);
      
      // Sort by 7-day engagement percentage (descending)
      const sortedUsers = [...topUsers].sort((a, b) => (b.sevenDayEngagement || 0) - (a.sevenDayEngagement || 0));
      
      // Simple treemap layout algorithm
      const createTreemap = (
        items: UserStats[], 
        x: number, 
        y: number, 
        width: number, 
        height: number
      ): SquarePosition[] => {
        if (items.length === 0) return [];
        if (items.length === 1) {
          return [{
            x,
            y,
            width,
            height,
            user: items[0],
            rank: 1
          }];
        }
        
        // Use 7-day engagement percentage for calculations
        const totalEngagement = items.reduce((sum, item) => sum + (item.sevenDayEngagement || 0), 0);
        const splitHorizontally = width > height;
        let currentSum = 0;
        let splitIndex = 0;
        
        for (let i = 0; i < items.length; i++) {
          currentSum += (items[i].sevenDayEngagement || 0);
          if (currentSum >= totalEngagement / 2) {
            splitIndex = i;
            break;
          }
        }
        
        const group1 = items.slice(0, splitIndex + 1);
        const group2 = items.slice(splitIndex + 1);
        const ratio = group1.reduce((sum, item) => sum + (item.sevenDayEngagement || 0), 0) / totalEngagement;
        
        if (splitHorizontally) {
          const group1Width = width * ratio;
          const group2Width = width - group1Width;
          return [
            ...createTreemap(group1, x, y, group1Width, height),
            ...createTreemap(group2, x + group1Width, y, group2Width, height)
          ];
        } else {
          const group1Height = height * ratio;
          const group2Height = height - group1Height;
          return [
            ...createTreemap(group1, x, y, width, group1Height),
            ...createTreemap(group2, x, y + group1Height, width, group2Height)
          ];
        }
      };
      
      const treemap = createTreemap(sortedUsers, 0, 0, width, height);
      
      const rankedTreemap = treemap.map((square, index) => ({
        ...square,
        rank: sortedUsers.findIndex(user => user.username === square.user.username) + 1
      }));
      
      setSquares(rankedTreemap);
    };
  }, [userStats]);

  // Set up resize observer for responsive layout
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Clean up previous observer if it exists
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }
    
    // Create new observer
    resizeObserverRef.current = new ResizeObserver(() => {
      calculateSquares();
    });
    
    // Start observing
    resizeObserverRef.current.observe(containerRef.current);
    
    // Initial calculation
    calculateSquares();
    
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [calculateSquares]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      calculateSquares();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateSquares]);

  const handleUserClick = (username: string) => {
    navigate(`/kolscan/${username}`);
  };

  return (
    <div className="space-y-6">
      <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
        <div className="flex items-center space-x-3 mb-6">
          <Users className="w-8 h-8 text-cyan-400" />
          <h1 className="text-2xl md:text-3xl font-bold">KOLScan</h1>
        </div>

        <div className="mb-6">
          <p className="text-gray-300 mb-4">
            Explore Key Opinion Leaders (KOLs) in the Bittensor ecosystem. Discover influential figures, track engagement metrics, and identify the most active voices in the community.
          </p>
          
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-3">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="w-5 h-5 text-cyan-400"
              >
                <path d="M4 4l11.733 16h4.267l-11.733 -16z"/>
                <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/>
              </svg>
              <h2 className="text-lg md:text-xl font-bold">KOL Engagement Visualization</h2>
            </div>
            
            {/* Removed the dropdown since we always show 50 */}
            <div className="text-sm text-gray-400">
              Showing top 50 KOLs
            </div>
          </div>
          
          <div 
            ref={containerRef} 
            className="relative bg-black/20 backdrop-blur-sm rounded-lg border border-white/10"
            style={{ height: `${containerSize.height}px` }}
          >
            {loadingStats ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : userStats.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-gray-400">No KOL data available</div>
              </div>
            ) : (
              squares.map((square) => {
                const opacity = 0.2 + ((square.user.sevenDayEngagement || 0) * 0.03);
                const hasProfileImage = !!square.user.profile_image;
                
                return (
                  <div
                    key={square.user.username}
                    className={`absolute flex items-center justify-center transition-all duration-300 cursor-pointer
                      ${hoveredSquare === square ? 'ring-2 ring-white/30 z-50' : 'z-10'}`}
                    style={{
                      left: square.x,
                      top: square.y,
                      width: square.width,
                      height: square.height,
                      backgroundColor: hasProfileImage ? `rgba(0, 0, 0, ${opacity})` : `rgba(30, 30, 30, ${opacity})`,
                      backgroundImage: hasProfileImage ? `url(${square.user.profile_image})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      transform: hoveredSquare === square ? 'scale(1.02)' : 'scale(1)',
                      border: '1px solid rgba(0, 0, 0, 0.2)'
                    }}
                    onMouseEnter={() => setHoveredSquare(square)}
                    onMouseLeave={() => setHoveredSquare(null)}
                    onClick={() => handleUserClick(square.user.username)}
                  >
                    {!hasProfileImage && (
                      <div className="w-1/3 h-1/3 rounded-full bg-black/40 flex items-center justify-center">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="24" 
                          height="24" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="w-1/2 h-1/2 text-white/70"
                        >
                          <path d="M4 4l11.733 16h4.267l-11.733 -16z"/>
                          <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/>
                        </svg>
                      </div>
                    )}
                    
                    <div className="absolute bottom-0 left-0 right-0 z-20">
                      <div className="text-center pb-1 px-1 bg-black/70 backdrop-blur-sm">
                        <div className="text-white font-bold text-xs truncate" style={{ textShadow: '0px 2px 4px rgba(0,0,0,0.9)' }}>
                          @{square.user.username}
                        </div>
                        <div className="flex items-center justify-center text-white/90 text-xs truncate" style={{ textShadow: '0px 2px 4px rgba(0,0,0,0.9)' }}>
                          <span>Rank {square.rank}</span>
                          <span className={`ml-1 flex items-center ${
                            square.user.percent_change_24h >= 0 ? 'text-green-400' : 'text-red-400'
                          }`} style={{ textShadow: '0px 1px 3px rgba(0,0,0,1)' }}>
                            {square.user.percent_change_24h >= 0 ? ' +' : ' '}{Math.abs(square.user.percent_change_24h).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {hoveredSquare === square && (
                      <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-black/90 backdrop-blur-sm rounded-lg p-2 text-white text-xs whitespace-nowrap z-50 border border-white/20 shadow-lg">
                        <div className="font-bold">7-Day Engagement: {(square.user.sevenDayEngagement || 0).toFixed(2)}%</div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        
        <div className="mt-6 text-sm text-gray-400">
          <p>
            Data is refreshed daily. Follow these KOLs to stay updated on the latest developments in the Bittensor ecosystem.
          </p>
          <p className="mt-2">
            <a 
              href="https://x.com/SwordScanAI" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-cyan-400 hover:underline inline-flex items-center"
            >
              Follow SwordScan on X
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="w-3 h-3 ml-1"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}